import { format, parse, startOfDay, differenceInMinutes, addMinutes } from 'date-fns';

export const SLOT_MINUTES = 30;
/** Pixels per 30-minute row — taller rows give labels room for title + meta without clipping */
export const SLOT_HEIGHT_PX = 68;
/** Dedicated row below the last bookable slot — shows closing time with breathing room */
export const GRID_CLOSE_FOOTER_PX = 52;

export function getGridSlotsHeightPx(grid) {
  if (!grid?.slots?.length) return 0;
  return grid.slots.length * SLOT_HEIGHT_PX;
}

export function getGridBodyHeightPx(grid) {
  return getGridSlotsHeightPx(grid) + GRID_CLOSE_FOOTER_PX;
}

const parseTimeOnDay = (baseDay, value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return new Date(
      baseDay.getFullYear(),
      baseDay.getMonth(),
      baseDay.getDate(),
      value.getHours(),
      value.getMinutes(),
      0,
      0
    );
  }
  const str = String(value).trim();
  if (!str) return null;
  let d = parse(str, 'HH:mm', baseDay);
  if (!isNaN(d.getTime())) return d;
  d = parse(str, 'h:mm a', baseDay);
  if (!isNaN(d.getTime())) return d;
  d = parse(str, 'h:mm aa', baseDay);
  if (!isNaN(d.getTime())) return d;
  return null;
};

/**
 * @param {Date} selectedDate
 * @param {Record<string, any>} companyHours
 * @returns {{ gridStart: Date, gridEnd: Date, slots: { time: Date, isHourStart: boolean }[] } | null}
 */
export function getDayTimeGrid(selectedDate, companyHours) {
  if (!selectedDate || !companyHours) return null;

  const dayKey = format(selectedDate, 'EEEE').toLowerCase();
  const day = companyHours[dayKey];
  if (!day?.isOpen) return null;

  const base = startOfDay(selectedDate);
  const openRaw = day.openTime ?? day.open ?? '09:00';
  const closeRaw = day.closeTime ?? day.close ?? '17:00';

  let gridStart = parseTimeOnDay(base, openRaw);
  let gridEnd = parseTimeOnDay(base, closeRaw);

  if (!gridStart) gridStart = parse('09:00', 'HH:mm', base);
  if (!gridEnd) gridEnd = parse('17:00', 'HH:mm', base);

  if (!gridStart || !gridEnd || gridEnd <= gridStart) return null;

  const slots = [];
  let t = gridStart;
  while (t < gridEnd) {
    const mins = t.getMinutes();
    slots.push({
      time: t,
      isHourStart: mins === 0,
    });
    t = addMinutes(t, SLOT_MINUTES);
  }

  return { gridStart, gridEnd, slots };
}

export function getAppointmentBounds(appointment) {
  const start = appointment.dateTime;
  if (!start || isNaN(start.getTime())) return null;
  let end = appointment.endTime;
  if (!end || isNaN(end.getTime())) {
    const dur = typeof appointment.duration === 'number' ? appointment.duration : 60;
    end = addMinutes(start, dur);
  }
  if (end <= start) return null;
  return { start, end };
}

/** Clip to grid; return null if no overlap */
export function layoutBlockPixels(start, end, gridStart, gridEnd) {
  const g0 = gridStart.getTime();
  const g1 = gridEnd.getTime();
  let s = start.getTime();
  let e = end.getTime();
  if (e <= g0 || s >= g1) return null;
  s = Math.max(s, g0);
  e = Math.min(e, g1);
  const visStart = new Date(s);
  const visEnd = new Date(e);
  const minutesFromStart = differenceInMinutes(visStart, gridStart);
  const durationMinutes = Math.max(differenceInMinutes(visEnd, visStart), 15);
  const topPx = (minutesFromStart / SLOT_MINUTES) * SLOT_HEIGHT_PX;
  const heightPx = Math.max(
    (durationMinutes / SLOT_MINUTES) * SLOT_HEIGHT_PX,
    SLOT_HEIGHT_PX * 0.9
  );
  return { topPx, heightPx };
}

const OVERLAP_INDENT_PCT = 5;

function clusterOverlapping(items) {
  const sorted = [...items].sort((a, b) => {
    const d = a.start - b.start;
    return d !== 0 ? d : b.end - a.end - (a.end - a.start);
  });

  const clusters = [];
  sorted.forEach((item) => {
    const matching = [];
    clusters.forEach((cluster, idx) => {
      const connected = cluster.some(
        (other) => item.start < other.end && item.end > other.start
      );
      if (connected) matching.push(idx);
    });

    if (matching.length === 0) {
      clusters.push([item]);
      return;
    }

    const merged = [item];
    matching.reverse().forEach((idx) => {
      merged.push(...clusters[idx]);
      clusters.splice(idx, 1);
    });
    clusters.push(merged);
  });

  return clusters;
}

function assignOverlapLanes(cluster) {
  const sorted = [...cluster].sort((a, b) => {
    const d = a.start - b.start;
    return d !== 0 ? d : b.end - a.end - (a.end - a.start);
  });

  const laneEnds = [];
  return sorted.map((item) => {
    let lane = laneEnds.findIndex((endMs) => endMs <= item.start.getTime());
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(0);
    }
    laneEnds[lane] = item.end.getTime();
    return { ...item, lane, laneCount: laneEnds.length };
  });
}

/**
 * Position overlapping appointments with a slight horizontal offset instead of
 * squeezing them into narrow side-by-side columns.
 */
export function layoutOverlappingBlocks(items, gridStart, gridEnd) {
  const placed = [];

  clusterOverlapping(items).forEach((cluster) => {
    const assignments = assignOverlapLanes(cluster);
    const laneCount = assignments.reduce((max, row) => Math.max(max, row.laneCount), 1);

    assignments.forEach(({ appointment, start, end, lane }) => {
      const layout = layoutBlockPixels(start, end, gridStart, gridEnd);
      if (!layout) return;

      const widthPct =
        laneCount > 1 ? 94 - (laneCount - 1) * OVERLAP_INDENT_PCT : 94;
      const leftPct = laneCount > 1 ? 3 + lane * OVERLAP_INDENT_PCT : 3;

      placed.push({
        appointment,
        style: {
          top: layout.topPx,
          height: layout.heightPx,
          width: `${widthPct}%`,
          left: `${leftPct}%`,
          zIndex: lane + 1,
        },
      });
    });
  });

  return placed;
}

/** @deprecated Use layoutOverlappingBlocks for grid placement */
export function groupOverlapping(items) {
  const sorted = [...items].sort((a, b) => {
    const d = a.start - b.start;
    return d !== 0 ? d : b.end - a.end - (a.end - a.start);
  });

  const groups = [];
  let current = [];

  sorted.forEach((apt) => {
    if (current.length === 0) {
      current.push(apt);
      return;
    }
    const overlaps = current.some(
      (other) => apt.start < other.end && apt.end > other.start
    );
    if (overlaps) current.push(apt);
    else {
      groups.push(current);
      current = [apt];
    }
  });
  if (current.length) groups.push(current);
  return groups;
}
